import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { operationsDashboardStyles } from "./styles";
import { MultiSelectDropdown, SingleSelectDropdown } from "../FilterDropdowns";
import MonthYearRangePicker from "./MonthRangePicker";
import { FilterComponentProps, FilterComponentRef } from "./type";
import CloseIcon from "@mui/icons-material/Close";
import { getDefaultDateRange } from "./utils";
import { UnitHierarchyFilter } from "./components/UnitHierarchyFilter/ServiceLineTreeFilter";

const FilterComponent = React.forwardRef<
  FilterComponentRef,
  FilterComponentProps
>(function FilterComponent(
  {
    values,
    appliedValues,
    onAccountsChange,
    onCustomersChange,
    onVerticalsChange,
    onBusinessUnitChange,
    onServiceLineChange,
    onDateRangeChange,
    onApply,
    onClearAll,
    isMobile = false,
    onClose,
    businessUnitsLoading = false,
    verticalOptions = [],
    verticalOptionsLoading = false,
    customerGroupOptions = [],
    customerGroupOptionsLoading = false,
    customerOptions = [],
    customerOptionsLoading = false,
    serviceLineFilterTree = null,
    businessUnitFilterTree = null,
    serviceLineOptionsLoading = false,
    userHasChanged,
  },
  ref,
) {
  const [dateResetCounter, setDateResetCounter] = React.useState(0);
  const [filtersApplyEpoch, setFiltersApplyEpoch] = React.useState(0);

  const handleMonthRangeChange = (start: any, end: any) => {
    const label =
      start && end
        ? `${start.format("MMM YYYY")} - ${end.format("MMM YYYY")}`
        : undefined;
    onDateRangeChange(start, end, label);
  };

  const handleClearAll = () => {
    setFiltersApplyEpoch((n) => n + 1);
    const { startDate, endDate, label } = getDefaultDateRange();

    onAccountsChange([]);
    onCustomersChange([]);
    onVerticalsChange("");
    onBusinessUnitChange("");
    onServiceLineChange("");
    onDateRangeChange(startDate, endDate, label);
    setDateResetCounter((c) => c + 1);
  };

  React.useImperativeHandle(ref, () => ({
    clearAll: handleClearAll,
  }));

  const hasChanges = React.useMemo(() => {
    if (!appliedValues) return false;

    const arraysEqual = (a: string[], b: string[]) => {
      if (a.length !== b.length) return false;
      const sortedA = [...a].sort((x, y) => x.localeCompare(y));
      const sortedB = [...b].sort((x, y) => x.localeCompare(y));
      return sortedA.every((val, index) => val === sortedB[index]);
    };

    return (
      !arraysEqual(values.accounts, appliedValues.accounts) ||
      !arraysEqual(values.customers, appliedValues.customers) ||
      values.verticals !== appliedValues.verticals ||
      values.businessUnit !== appliedValues.businessUnit ||
      values.serviceLine !== appliedValues.serviceLine ||
      values.dateLabel !== appliedValues.dateLabel
    );
  }, [values, appliedValues]);

  const hasCurrentFilters = React.useMemo(() => {
    return (
      values.accounts.length > 0 ||
      values.customers.length > 0 ||
      Boolean(values.verticals) ||
      Boolean(values.businessUnit) ||
      Boolean(values.serviceLine) ||
      Boolean(values.dateLabel)
    );
  }, [values]);

  const applyButtonEnabled =
    userHasChanged !== undefined ? userHasChanged : hasChanges;
  const clearButtonEnabled = hasCurrentFilters;
  const showSubFilters = Boolean(
    values.businessUnit && values.businessUnit !== "",
  );

  return (
    <>
      <Box sx={operationsDashboardStyles.filterHeader}>
        <Typography variant="h6" sx={operationsDashboardStyles.sectionTitle}>
          Filters
        </Typography>
        {isMobile && onClose && (
          <CloseIcon
            onClick={onClose}
            sx={operationsDashboardStyles.filterCloseIcon}
          />
        )}
      </Box>
      <Box>
        <Box sx={operationsDashboardStyles.filterFieldBox}>
          <MonthYearRangePicker
            key={`${dateResetCounter}-${values.dateLabel ?? "empty"}`}
            onChange={handleMonthRangeChange}
            initialStartMonth={values.dateStart}
            initialEndMonth={values.dateEnd}
          />
        </Box>

        <Box sx={operationsDashboardStyles.filterFieldBox}>
          <UnitHierarchyFilter
            tree={businessUnitFilterTree}
            serviceLine={values.businessUnit}
            onChange={onBusinessUnitChange}
            loading={businessUnitsLoading}
            disabled={businessUnitsLoading}
            filtersApplyEpoch={filtersApplyEpoch}
            fieldLabel="Business Unit"
            emptySearchMessage="No matching business units"
            inputName="business-unit-filter"
            panelId="business-unit-tree-panel"
            clearSelectionAriaLabel="Clear business unit"
            defaultExpandedRootSectionName="BU Combined"
          />
        </Box>

        <Box sx={operationsDashboardStyles.filterFieldBox}>
          <UnitHierarchyFilter
            tree={serviceLineFilterTree}
            serviceLine={values.serviceLine}
            onChange={onServiceLineChange}
            loading={serviceLineOptionsLoading}
            disabled={serviceLineOptionsLoading}
            filtersApplyEpoch={filtersApplyEpoch}
          />
        </Box>

        {showSubFilters && (
          <Box sx={operationsDashboardStyles.filterFieldBox}>
            <SingleSelectDropdown
              placeholder={`${verticalOptions.length} Options`}
              onSelect={onVerticalsChange}
              selectedValue={values.verticals}
              loading={verticalOptionsLoading}
              disabled={verticalOptionsLoading}
              label="Verticals"
              options={verticalOptions}
              labelMarginBottom="0"
            />
          </Box>
        )}

        {showSubFilters && (
          <MultiSelectDropdown
            label={"Customer Group"}
            placeholder={`${customerGroupOptions.length} Options`}
            options={customerGroupOptions}
            onSelect={(selectedValues) => onAccountsChange(selectedValues)}
            selectedValues={values.accounts}
            loading={customerGroupOptionsLoading}
            disabled={customerGroupOptionsLoading}
          />
        )}

        {showSubFilters && (
          <MultiSelectDropdown
            label={"Customer"}
            placeholder={`${customerOptions.length} Options`}
            options={customerOptions}
            onSelect={(selectedValues) => onCustomersChange(selectedValues)}
            selectedValues={values.customers}
            loading={customerOptionsLoading}
            disabled={customerOptionsLoading}
          />
        )}
      </Box>

      <Box sx={operationsDashboardStyles.filterActionsBox}>
        <Button
          onClick={() => {
            setFiltersApplyEpoch((n) => n + 1);
            onApply?.();
          }}
          disabled={!applyButtonEnabled}
          sx={operationsDashboardStyles.applyButton}
          variant="contained"
          color="primary"
        >
          Apply
        </Button>
        <Button
          sx={operationsDashboardStyles.clearButton}
          onClick={onClearAll}
          disabled={!clearButtonEnabled}
          variant="outlined"
        >
          Clear filters
        </Button>
      </Box>
    </>
  );
});

export default FilterComponent;
